import { useState, useCallback, useMemo, useEffect } from "react";
import { Post } from "@/core/types/postTypes";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import {
  PostMediaSection,
  PostOverlay,
  PostBadge,
  PostCarouselMedia,
  useCarouselTapNav,
} from "@/components/PostCard";
import { usePostCardAuthOptional } from "@/domains/PostCreator/contexts/PostCardAuthContext";
import { UserRole } from "@/services/urlService";
import { clientService } from "@/domains/Approval/services/clientService";
import { EditIcon, TrashIcon, CheckIcon, ExpandIcon, CloseIcon } from "@/components/icons";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Modal } from "@/components/ui/Modal";

interface ClientFeedPostProps {
  post: Post;
  gridId: string | number;
  clientId: string;
  isFocused?: boolean;
  /**
   * Toggle the client-review stamp on the post. Available on the
   * dedicated review page; absent in the planning-grid embedding where
   * the like/dislike sentiment buttons are the primary interaction.
   */
  onReviewPost?: (postId: string, isReviewed: boolean) => Promise<void>;
  onPostUpdated?: () => void;
}

interface NoteData {
  id: string;
  content: string;
  createdAt: string;
}

export function ClientFeedPost({
  post,
  gridId,
  clientId,
  isFocused = false,
  onReviewPost,
  onPostUpdated,
}: ClientFeedPostProps) {
  const authContext = usePostCardAuthOptional();

  // Media state — fullscreen expand toggle (carousel index lives in the
  // shared useCarouselTapNav hook, once the media count is known).
  const [isExpanded, setIsExpanded] = useState(false);

  // Caption editing state
  const [isEditingCaption, setIsEditingCaption] = useState(false);
  const [editedCaption, setEditedCaption] = useState(post.caption || "");
  const [isSavingCaption, setIsSavingCaption] = useState(false);

  // Review toggle state. Source of truth is `post.clientReviewedAt`
  // from the server; local state tracks in-flight clicks so the button
  // doesn't flicker mid-request.
  const [isReviewing, setIsReviewing] = useState(false);
  const isReviewed = Boolean(post.clientReviewedAt);

  const handleToggleReview = useCallback(async () => {
    if (!onReviewPost || isReviewing) return;
    setIsReviewing(true);
    try {
      await onReviewPost(post.id, !isReviewed);
    } catch (err) {
      console.error("Failed to toggle review:", err);
    } finally {
      setIsReviewing(false);
    }
  }, [onReviewPost, post.id, isReviewed, isReviewing]);

  // Notes state
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [notes, setNotes] = useState<NoteData[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const [isDeletingNote, setIsDeletingNote] = useState(false);

  // Convert and sort media items
  const sortedMediaItems = useMemo(() => {
    if (!post?.mediaItems || post.mediaItems.length === 0) return [];

    const userRole = authContext?.userRole || UserRole.CLIENT;
    const resolvedClientId = authContext?.clientId || clientId;
    const authToken = authContext?.authToken;

    if (!resolvedClientId) return [];
    if (userRole === UserRole.CLIENT && (!authToken || authToken.trim() === "")) return [];

    const instances = post.mediaItems.map((item) => {
      const backendData = item instanceof MediaItem ? item.backendData : item;
      return MediaItem.fromBackend(backendData, userRole, resolvedClientId, authToken);
    });

    return instances.sort((a, b) => (a.position ?? 999) - (b.position ?? 999));
  }, [post?.mediaItems, authContext, clientId]);

  const imageCount = sortedMediaItems?.length || 0;
  const { index: currentImageIndex, goPrev, goNext } = useCarouselTapNav(imageCount);

  // Get current image for display
  const getCurrentImage = useCallback(() => {
    if (post.postType === "reel") {
      if (post.thumbnailMediaItem) return post.thumbnailMediaItem;
      if (post.thumbnailMediaId && sortedMediaItems) {
        const thumbnailId =
          typeof post.thumbnailMediaId === "string"
            ? parseInt(post.thumbnailMediaId)
            : post.thumbnailMediaId;
        const thumbnailItem = sortedMediaItems.find((item) => item.id === thumbnailId);
        if (thumbnailItem && thumbnailItem.isImage()) return thumbnailItem;
      }
      const imageItem = sortedMediaItems.find((item) => item.isImage());
      if (imageItem) return imageItem;
      return sortedMediaItems.find((item) => item.isVideo()) || null;
    }

    if (!sortedMediaItems || sortedMediaItems.length === 0) return null;

    if (post.isCarousel && sortedMediaItems.length > 1) {
      const idx = Math.min(currentImageIndex, sortedMediaItems.length - 1);
      return sortedMediaItems[idx];
    }

    return sortedMediaItems[0];
  }, [sortedMediaItems, post.isCarousel, post.postType, post.thumbnailMediaItem, currentImageIndex]);

  const getFocusedVideoItem = useCallback(() => {
    // For reels: any video (carousels of reels are unusual; first video wins).
    // For other types with a single video: that video.
    // For carousels: only autoplay video when the current carousel slide is the video.
    if (post.postType === "reel") {
      return sortedMediaItems.find((item) => item.isVideo()) || null;
    }
    if (post.isCarousel && sortedMediaItems.length > 1) {
      const idx = Math.min(currentImageIndex, sortedMediaItems.length - 1);
      const current = sortedMediaItems[idx];
      return current?.isVideo() ? current : null;
    }
    const single = sortedMediaItems[0];
    return single?.isVideo() ? single : null;
  }, [post.postType, post.isCarousel, sortedMediaItems, currentImageIndex]);

  const displayMediaItem = useMemo(() => {
    if (isFocused) {
      const videoItem = getFocusedVideoItem();
      if (videoItem) return videoItem;
    }
    return getCurrentImage();
  }, [isFocused, getFocusedVideoItem, getCurrentImage]);

  // A non-reel carousel with more than one item gets the Instagram-style tap
  // zones; the current slide autoplays inline when it's a focused video.
  const isMultiCarousel =
    post.isCarousel && post.postType !== "reel" && imageCount > 1;
  const isVideoSlide = isFocused && !!displayMediaItem?.isVideo();

  // Load notes on mount
  useEffect(() => {
    loadNotes();
  }, [clientId, post.id]);

  const loadNotes = async () => {
    try {
      setLoadingNotes(true);
      const result = await clientService.getNotes(clientId, post.id);
      setNotes(result);
    } catch (error) {
      console.error("Failed to load notes:", error);
    } finally {
      setLoadingNotes(false);
    }
  };

  // Caption editing
  const handleStartEditCaption = () => {
    setEditedCaption(post.caption || "");
    setIsEditingCaption(true);
  };

  const handleSaveCaption = async () => {
    try {
      setIsSavingCaption(true);
      await clientService.updatePost(clientId, post.id, { caption: editedCaption });
      setIsEditingCaption(false);
      onPostUpdated?.();
    } catch (error) {
      console.error("Failed to save caption:", error);
    } finally {
      setIsSavingCaption(false);
    }
  };

  const handleCancelEditCaption = () => {
    setIsEditingCaption(false);
    setEditedCaption(post.caption || "");
  };

  // Note adding
  const handleStartAddNote = () => {
    setNoteContent("");
    setIsAddingNote(true);
  };

  const handleSaveNote = async () => {
    if (!noteContent.trim()) return;
    try {
      setIsSavingNote(true);
      const newNote = await clientService.addNote(clientId, post.id, noteContent.trim());
      setNotes((prev) => [...prev, newNote]);
      setIsAddingNote(false);
      setNoteContent("");
    } catch (error) {
      console.error("Failed to save note:", error);
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleCancelNote = () => {
    setIsAddingNote(false);
    setNoteContent("");
  };

  const handleStartEditNote = (note: NoteData) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteContent("");
  };

  const handleSaveEditNote = async () => {
    if (!editingNoteId || !editingNoteContent.trim()) return;
    try {
      setIsSavingEdit(true);
      const updated = await clientService.updateNote(
        clientId,
        post.id,
        editingNoteId,
        editingNoteContent.trim(),
      );
      setNotes((prev) => prev.map((n) => (n.id === editingNoteId ? updated : n)));
      setEditingNoteId(null);
      setEditingNoteContent("");
    } catch (error) {
      console.error("Failed to update note:", error);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteNoteConfirm = async () => {
    if (!noteToDelete) return;
    try {
      setIsDeletingNote(true);
      await clientService.deleteNote(clientId, post.id, noteToDelete);
      setNotes((prev) => prev.filter((n) => n.id !== noteToDelete));
      setNoteToDelete(null);
    } catch (error) {
      console.error("Failed to delete note:", error);
    } finally {
      setIsDeletingNote(false);
    }
  };

  const overlayButtonClass =
    "absolute top-2 right-2 z-30 inline-flex items-center justify-center rounded-full bg-black/50 p-2 text-white backdrop-blur-sm transition hover:bg-black/70 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-white/70";

  const carouselDots =
    post.postType !== "reel" && post.isCarousel && imageCount > 1 ? (
      <PostOverlay.BottomRight className="pointer-events-none">
        <PostBadge.CarouselIndicator
          currentIndex={currentImageIndex}
          totalItems={imageCount}
        />
      </PostOverlay.BottomRight>
    ) : null;

  // Media stage, shared by the card and the fullscreen expand view. Only the
  // visible surface plays: while the modal is open the card behind it renders
  // a still, so a single <video> element is ever active.
  const renderMedia = (expanded: boolean) => {
    if (!displayMediaItem) {
      return (
        <div className="aspect-4/5 bg-gray-100 flex items-center justify-center">
          <span className="text-sm text-gray-500">Nessun media</span>
        </div>
      );
    }

    // In the card the focused video slide autoplays (and yields to the modal
    // when open); in the modal it plays because the viewer opened it.
    const videoActive = expanded
      ? displayMediaItem.isVideo()
      : isVideoSlide && !isExpanded;

    const overlays = (
      <>
        {carouselDots}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!expanded);
          }}
          aria-label={expanded ? "Chiudi" : "Ingrandisci"}
          className={overlayButtonClass}
        >
          {expanded ? (
            <CloseIcon className="h-5 w-5" />
          ) : (
            <ExpandIcon className="h-5 w-5" />
          )}
        </button>
      </>
    );

    if (isMultiCarousel) {
      return (
        <PostCarouselMedia
          currentItem={displayMediaItem}
          isVideoSlide={videoActive}
          showTapZones
          onPrev={goPrev}
          onNext={goNext}
          useThumbnail={!expanded}
        >
          {overlays}
        </PostCarouselMedia>
      );
    }

    return (
      <PostMediaSection
        viewMode="feed"
        mediaItem={displayMediaItem}
        isReel={post.postType === "reel"}
        isCarousel={post.isCarousel}
        autoPlay={videoActive}
      >
        {overlays}
      </PostMediaSection>
    );
  };

  // Approval is the primary action; edit-caption / add-note sit below it. All
  // buttons share the design-library token set (rounded-lg, font-medium,
  // focus-ring, 44px+ touch target). Extracted so the card and the expand
  // panel render the exact same controls.
  const actionButtonsInner = (
    <>
      {onReviewPost && (
            <button
              onClick={handleToggleReview}
              disabled={isReviewing}
              className={
                "w-full inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 " +
                (isReviewed
                  ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 focus-visible:ring-emerald-500"
                  : "bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-500")
              }
            >
              <CheckIcon className="w-4 h-4" />
              {isReviewed ? "Approvato — annulla" : "Approva post"}
            </button>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleStartEditCaption}
              className="flex-1 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
            >
              <EditIcon className="w-4 h-4" />
              Modifica Didascalia
            </button>
            <button
              onClick={handleStartAddNote}
              className="flex-1 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Aggiungi Nota
            </button>
          </div>
    </>
  );

  const captionContent = isEditingCaption ? (
          <div className="space-y-2">
            <textarea
              value={editedCaption}
              onChange={(e) => setEditedCaption(e.target.value)}
              className="w-full min-h-[120px] p-3 border border-gray-300 rounded-lg text-base resize-none focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Scrivi una didascalia..."
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveCaption}
                disabled={isSavingCaption}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium text-sm disabled:opacity-50 min-h-[44px]"
              >
                {isSavingCaption ? "Salvataggio..." : "Salva Didascalia"}
              </button>
              <button
                onClick={handleCancelEditCaption}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium text-sm min-h-[44px]"
              >
                Annulla
              </button>
            </div>
          </div>
        ) : (
          <div>
            {post.caption ? (
              <p className="text-base text-gray-900 leading-relaxed whitespace-pre-wrap">
                {post.caption}
              </p>
            ) : (
              <p className="text-base text-gray-400 italic">Nessuna didascalia</p>
            )}
          </div>
  );

  const notesList = notes.length > 0 ? (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-500">Note</h3>
            {notes.map((note) => {
              const isEditing = editingNoteId === note.id;
              return (
                <div
                  key={note.id}
                  className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
                >
                  {isEditing ? (
                    <>
                      <textarea
                        value={editingNoteContent}
                        onChange={(e) => setEditingNoteContent(e.target.value)}
                        className="w-full min-h-[80px] p-2 border border-amber-300 rounded-md text-sm resize-none focus:outline-hidden focus:ring-2 focus:ring-amber-500 bg-white"
                        autoFocus
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={handleSaveEditNote}
                          disabled={isSavingEdit || !editingNoteContent.trim()}
                          className="flex-1 py-2 bg-amber-600 text-white rounded-md font-medium text-sm disabled:opacity-50 min-h-[36px]"
                        >
                          {isSavingEdit ? "Salvataggio..." : "Salva"}
                        </button>
                        <button
                          onClick={handleCancelEditNote}
                          className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-md font-medium text-sm min-h-[36px]"
                        >
                          Annulla
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                        {note.content}
                      </p>
                      <div className="mt-2 flex items-center justify-between">
                        {note.createdAt ? (
                          <p className="text-xs text-gray-400">
                            {new Date(note.createdAt).toLocaleDateString("it-IT")}
                          </p>
                        ) : (
                          <span />
                        )}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleStartEditNote(note)}
                            aria-label="Modifica nota"
                            className="p-1.5 text-amber-700 hover:bg-amber-100 rounded-md"
                          >
                            <EditIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setNoteToDelete(note.id)}
                            aria-label="Elimina nota"
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-md"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
  ) : null;

  const addNoteForm = isAddingNote ? (
          <div className="space-y-2">
            <textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              className="w-full min-h-[80px] p-3 border border-gray-300 rounded-lg text-base resize-none focus:outline-hidden focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="Aggiungi una nota..."
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveNote}
                disabled={isSavingNote || !noteContent.trim()}
                className="flex-1 py-3 bg-amber-600 text-white rounded-lg font-medium text-sm disabled:opacity-50 min-h-[44px]"
              >
                {isSavingNote ? "Salvataggio..." : "Salva Nota"}
              </button>
              <button
                onClick={handleCancelNote}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium text-sm min-h-[44px]"
              >
                Annulla
              </button>
            </div>
          </div>
  ) : null;

  const confirmDialog = (
    <ConfirmDialog
      isOpen={noteToDelete !== null}
      onClose={() => setNoteToDelete(null)}
      onConfirm={handleDeleteNoteConfirm}
      title="Elimina nota"
      message="Sei sicuro di voler eliminare questa nota? L'operazione non è reversibile."
      confirmText="Elimina"
      variant="danger"
      isLoading={isDeletingNote}
    />
  );

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-2xl overflow-hidden shadow-xs flex flex-col">
      {renderMedia(false)}

      {/* Action buttons — between media and content. */}
      {!isEditingCaption && !isAddingNote && (
        <div className="p-4 pb-0 space-y-2">{actionButtonsInner}</div>
      )}

      {/* Content section */}
      <div className="p-4 space-y-4 pb-16">
        {captionContent}
        {notesList}
        {addNoteForm}
      </div>

      {confirmDialog}

      {/* Fullscreen expand: proportional letterboxed media + a compact panel
          with the caption and the same action buttons. */}
      <Modal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        size="lg"
        showCloseButton={false}
      >
        <div className="flex flex-col">
          {renderMedia(true)}
          <div className="p-4 space-y-3">
            {captionContent}
            {!isEditingCaption && !isAddingNote && (
              <div className="space-y-2">{actionButtonsInner}</div>
            )}
            {addNoteForm}
          </div>
        </div>
      </Modal>
    </div>
  );
}
